// Where the cat can stand in a model land — the rules, kept free of Three.js so a build check can run exactly the code the game runs.
//
// With a baked floor map: wherever that map has a floor. Otherwise: inside the walkable disc, off every collision footprint, not too steep, not out in a pond (unless on a bridge deck), and never a sudden
// step up or down: ledges stop the cat while ramps, stairs and the ground's own slopes, being gradual, let it through.
import { makeTerrain } from './terrainMath.js'

export function createWalker(layout, manifest, nav = null) {
  const T = layout.terrain ? makeTerrain(layout.terrain) : null
  const m0 = layout.meta
  const R = manifest.radiusM ?? 60
  const groundLevelM = manifest.groundLevelM ?? 0
  const waterList = layout.water ?? []
  const decks = layout.decks ?? []

  // collision footprints: precomputed oriented rectangles in a coarse spatial hash
  const CELL = 4
  const grid = new Map()
  const cols = layout.colliders ?? []
  cols.forEach((c, idx) => {
    const [cx, cz, hx, hz, ax, az] = c
    const ex = Math.abs(ax) * hx + Math.abs(az) * hz
    const ez = Math.abs(az) * hx + Math.abs(ax) * hz
    for (let ix = Math.floor((cx - ex) / CELL); ix <= Math.floor((cx + ex) / CELL); ix++) {
      for (let iz = Math.floor((cz - ez) / CELL); iz <= Math.floor((cz + ez) / CELL); iz++) {
        const k = `${ix},${iz}`
        let l = grid.get(k)
        if (!l) grid.set(k, (l = []))
        l.push(idx)
      }
    }
  })
  const padM = manifest.heroRadiusM ?? 0.45
  const limit = R - (manifest.walkMarginM ?? 3)
  const walkC = manifest.walkCenter ? { x: manifest.walkCenter[0], z: manifest.walkCenter[1] } : m0.spawn // (the walkable disc is centred here; defaults to the spawn)
  const standableM = (mx, mz) => {
    if (Math.hypot(mx - walkC.x, mz - walkC.z) > limit) return false
    const list = grid.get(`${Math.floor(mx / CELL)},${Math.floor(mz / CELL)}`)
    if (!list) return true
    for (const idx of list) {
      const [cx, cz, hx, hz, ax, az] = cols[idx]
      const dx = mx - cx, dz = mz - cz
      if (Math.abs(dx * ax + dz * az) <= hx + padM && Math.abs(-dx * az + dz * ax) <= hz + padM) return false
    }
    return true
  }

  const maxSlope = manifest.maxSlope ?? 0.85, stepM = manifest.stepM ?? 0.9, wadeM = 0.45
  const D2R = Math.PI / 180
  const waterY = (mx, mz) => {
    let y = null
    for (const w of waterList) {
      const dx = mx - w.x, dz = mz - w.z
      let inside
      if (w.r) inside = Math.hypot(dx, dz) < w.r
      else {
        const c = Math.cos((w.yawDeg ?? 0) * D2R), s = Math.sin((w.yawDeg ?? 0) * D2R), lx = dx * c - dz * s, lz = dx * s + dz * c
        inside = w.rx ? Math.hypot(lx / w.rx, lz / w.rz) < 1 : Math.abs(lx) < w.hx && Math.abs(lz) < w.hz
      }
      if (inside) y = Math.max(y ?? -Infinity, w.y)
    }
    return y
  }
  const deckY = (d, mx, mz) => {
    const dx = mx - d.x, dz = mz - d.z, c = Math.cos((d.yawDeg ?? 0) * D2R), s = Math.sin((d.yawDeg ?? 0) * D2R)
    const lx = dx * c - dz * s, lz = dx * s + dz * c
    if (Math.abs(lx) > d.hx || Math.abs(lz) > d.hz) return null
    return d.y0 + ((d.y1 ?? d.y0) - d.y0) * ((lx + d.hx) / (2 * d.hx))
  }
  // the floor height (metres) the cat would stand at in this spot, given the height it is at now (null = unknown, e.g. at spawn), or null if it can't stand there
  // With a baked floor map (nav — the same map Capital City uses) that decides everything: stairs, bridges, gates, floors inside buildings.
  const heroR = manifest.heroRadiusM ?? 0.45, heroClear = manifest.heroClearM ?? 0.9
  const floorM = (mx, mz, curM) => {
    if (nav) return nav.step(mx, mz, curM ?? manifest.groundLevelM ?? 0, heroR, heroClear)
    if (!standableM(mx, mz)) return null
    const cands = []
    if (!T) cands.push(groundLevelM)
    else {
      const ty = T.heightAt(mx, mz), wy = waterY(mx, mz)
      if (T.slopeAt(mx, mz) <= maxSlope && !(wy !== null && ty < wy - wadeM)) cands.push(ty)
    }
    for (const d of decks) { const y = deckY(d, mx, mz); if (y !== null) cands.push(y) }
    if (!cands.length) return null
    if (curM === null) return Math.max(...cands)
    let best = null
    for (const y of cands) if (Math.abs(y - curM) <= stepM && (best === null || Math.abs(y - curM) < Math.abs(best - curM))) best = y
    return best
  }
  const heightAt = (mx, mz) => (T ? T.heightAt(mx, mz) : groundLevelM)
  return { T, floorM, standableM, heightAt, walkC, limit, stepM }
}
